/**
 * PricingPage — the public /pricing page (#107-adjacent / punch-list B7).
 *
 * Built against the locked design design/mockups/V1_Pricing_Page_v1.html,
 * populated from the locked v1.0 pricing model (V1_Pricing_Page_Content_Draft
 * + V1_Stripe_Products_Prices_Engineering_Brief). Replaces the noindex
 * marketing scaffold for /pricing — this is real, indexable content.
 *
 * Billing toggle: monthly / annual is React state (the mockup did it with a
 * body class + CSS show/hide). Annual = 2 months free.
 *
 * CTAs — demo-led for V1 (re-synced 2026-05-23 against the revised
 * demo-led V1_Pricing_Page_v1.html). PeoplePrints' onboarding is invite/
 * provisioning-based; there is no self-serve "create your own tenant +
 * subscribe" flow, and billing-checkout.js is auth + tenant-admin gated.
 * So every tier CTA routes to /demo (sales-assisted start) and reads
 * "Request a demo" / Enterprise "Talk to us about Enterprise". A
 * self-serve signup→Checkout flow is a separate V1.x fast-follow (B14,
 * V1_Engineering_Followups).
 *
 * The app shell's <TopNav> renders the nav; this page is content + footer.
 * Scoped <style> block (media queries can't live in inline styles); colors
 * alias onto locked --ppr-* tokens.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "../../lib/use-page-meta.js";

const PRICING_DESCRIPTION =
  "Three plans metered on what you actually use — employees you manage, candidates you evaluate, seats on your team. Recruiter $49, Manager $149, HR Director $349. Monthly or annual.";

const TIERS = [
  {
    name: "Recruiter",
    monthly: "$49",
    annual: "$490",
    builtFor:
      "The recruiter running a real pipeline. A tactical read on every candidate before the screen.",
    includes: [
      "40 candidate runs / month — a Predisposition Profile + Interview Blueprint for each",
      "5 active employee profiles — keep reads on your hiring managers and hiring team",
      "1 seat",
      "Every Blueprint type included",
    ],
    cta: "Request a demo",
    ctaTopic: "recruiter",
    featured: false,
  },
  {
    name: "Manager",
    monthly: "$149",
    annual: "$1,490",
    builtFor:
      "The manager leading a team — reviews, 1-on-1s, cross-functional influence.",
    includes: [
      "15 active employee profiles — your team, your peers, your cross-functional partners",
      "100 candidate runs / month — for the hiring you do",
      "2 seats",
      "Every Blueprint type — Interview, Performance Review, Operating Manual, Relational, and more",
    ],
    cta: "Request a demo",
    ctaTopic: "manager",
    featured: false,
  },
  {
    name: "HR Director",
    monthly: "$349",
    annual: "$3,490",
    builtFor:
      "The HR Director who owns the full surface — interviews, performance, mediation, exits.",
    includes: [
      "40 active employee profiles",
      "200 candidate runs / month",
      "5 seats — for your HR team",
      "Every Blueprint type, including the Conflict Mediation Blueprint",
      "The governance layer — Fairness Audit, full audit log, HR-on-behalf-of generation",
    ],
    cta: "Request a demo",
    ctaTopic: "hr-director",
    featured: true,
  },
  {
    name: "Enterprise",
    fromAnnual: "$5,000",
    builtFor:
      "Organizations rolling PeoplePrints out beyond a single HR seat.",
    includes: [
      "Everything in HR Director",
      "Volume profile + candidate-run capacity, negotiated",
      "6+ seats with role-based access",
      "Custom HRIS integration (Workday, Greenhouse, Lever — V1.x onward)",
      "White-label Blueprint surface",
      "Quarterly business review with the founder",
    ],
    inheritsFirst: true,
    cta: "Talk to us about Enterprise",
    ctaTopic: "enterprise",
    featured: false,
  },
];

const FAQS = [
  {
    q: "Is there a seat minimum?",
    a: "No. Every plan starts at one seat. Add seats when you need them — $49/month each, or step up to the tier that includes more.",
  },
  {
    q: 'What’s the difference between an "employee profile" and a "candidate run"?',
    a: "An active employee profile is someone you manage — it holds a slot until you archive them. A candidate run is someone you evaluate for a role — it's counted once for the month. Same engine, two meters, because hiring and managing are two different jobs.",
  },
  {
    q: "What happens if I go over a cap?",
    a: "You get a quiet in-app notice. Nothing is auto-billed and nothing is paywalled. Extra employee profiles are $9/month each; extra candidate runs are $5 credit-pack runs; extra seats are $49/month. If you're consistently over, the next tier up is the better deal and we'll help you switch.",
  },
  {
    q: "Monthly or annual?",
    a: "Both. Monthly is month-to-month, cancel anytime. Annual bills once and gives you two months free. You can switch billing cadence at renewal.",
  },
  {
    q: "Can I see it before I commit?",
    a: "Yes — every plan starts with a demo. Book one and we'll walk you through PeoplePrints on a real example, then set your team up on the plan that fits. It's the fastest way to see it on the work you actually do.",
  },
  {
    q: "Can I downgrade or cancel?",
    a: "Anytime. Downgrades take effect at the next billing cycle; you keep higher-tier features through the cycle you paid for. Monthly plans cancel immediately at period end.",
  },
  {
    q: "Is the Conflict Mediation Blueprint really included on HR Director?",
    a: "Yes — it's a standard part of the HR Director plan, not an add-on. If you're an HR Director and you don't need it this quarter, you'll need it next quarter.",
  },
];

// FAQPage structured data — derived from FAQS so the schema text stays
// verbatim-matched to the on-page FAQ (a Google requirement for FAQ rich
// results). The build-time prerender captures it into the static /pricing
// HTML. Site-wide Organization/SoftwareApplication JSON-LD is in index.html.
const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FOOTER_LINKS = [
  { to: "/#how-it-works", label: "Product" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/for/recruiters", label: "For Recruiters" },
  { to: "/for/managers", label: "For Managers" },
  { to: "/for/hr-directors", label: "For HR Directors" },
  { to: "/for/founders", label: "For Founders" },
  { to: "/for/employee-relations", label: "For Employee Relations" },
  { to: "/methodology", label: "Methodology" },
  { to: "/about", label: "About" },
  { to: "/security-and-privacy", label: "Security & Privacy" },
  { to: "/demo", label: "Request a Demo" },
];

export default function PricingPage() {
  usePageMeta({
    title: "Pricing — PeoplePrints",
    description: PRICING_DESCRIPTION,
    canonicalPath: "/pricing",
  });

  const [annual, setAnnual] = useState(false);

  return (
    <div className="ppm-root">
      <style>{PRICING_CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      {/* ── Hero ── */}
      <section className="ppm-hero">
        <div className="ppm-hero-inner">
          <p className="ppm-hero-eyebrow">Pricing</p>
          <h1 className="ppm-hero-h1">
            Priced for the job you do — not the contract you have to sign.
          </h1>
          <p className="ppm-hero-sub">
            No seat floors. No annual lock-in to get started. Pick the plan
            that matches your role. Every plan includes every Blueprint type;
            the plans differ only in how many people you're working and how
            many seats you need. Upgrade when the job grows.
          </p>
        </div>
      </section>

      {/* ── Tiers ── */}
      <section className="ppm-block" style={{ paddingTop: 48 }}>
        <div
          className="ppm-billing-toggle"
          role="group"
          aria-label="Billing period"
        >
          <button
            type="button"
            className={`ppm-bt ${annual ? "" : "ppm-bt-on"}`}
            onClick={() => setAnnual(false)}
            aria-pressed={!annual}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`ppm-bt ${annual ? "ppm-bt-on" : ""}`}
            onClick={() => setAnnual(true)}
            aria-pressed={annual}
          >
            Annual <span className="ppm-bt-save">2 months free</span>
          </button>
        </div>

        <div className="ppm-pricing-grid">
          {TIERS.map((t) => (
            <div
              key={t.name}
              className={`ppm-tier ${t.featured ? "ppm-tier-featured" : ""}`}
            >
              {t.featured && <span className="ppm-tier-ribbon">Most popular</span>}
              <h3 className="ppm-tier-name">{t.name}</h3>
              <p className="ppm-tier-price">
                {t.fromAnnual ? (
                  <>
                    <span className="ppm-tier-from">From </span>
                    {t.fromAnnual}
                    <span className="ppm-tier-period"> / year</span>
                  </>
                ) : annual ? (
                  <>
                    {t.annual}
                    <span className="ppm-tier-period"> / year</span>
                  </>
                ) : (
                  <>
                    {t.monthly}
                    <span className="ppm-tier-period"> / month</span>
                  </>
                )}
              </p>
              <p className="ppm-tier-builtfor">{t.builtFor}</p>
              <ul className="ppm-tier-includes">
                {t.includes.map((line, i) => (
                  <li
                    key={line}
                    className={t.inheritsFirst && i === 0 ? "ppm-inherits" : ""}
                  >
                    {line}
                  </li>
                ))}
              </ul>
              <Link
                to={`/demo?topic=${t.ctaTopic}`}
                className={`ppm-tier-cta ${t.featured ? "ppm-cta-primary" : "ppm-cta-secondary"}`}
              >
                {t.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* ── How metering works ── */}
      <section className="ppm-block ppm-compact ppm-tinted">
        <div className="ppm-block-inner">
          <p className="ppm-eyebrow">How metering works</p>
          <h2 className="ppm-h2">Three things you're paying for. That's it.</h2>
          <p className="ppm-body">
            PeoplePrints meters three things, and only three.{" "}
            <strong>Active employee profiles</strong> — the people you manage;
            a profile holds a slot until you archive it, and archiving a
            departed employee frees the slot back up.{" "}
            <strong>Candidate runs</strong> — each candidate you evaluate in a
            month. <strong>Seats</strong> — the people on your team who log in.
            Everything else — every Blueprint, on every plan, as many as you
            need for the people you're working — is included. We don't meter
            Blueprints. You're paying for people and seats, not for outputs.
          </p>
        </div>
      </section>

      {/* ── Overages ── */}
      <section className="ppm-block ppm-compact">
        <div className="ppm-block-inner">
          <p className="ppm-eyebrow">Overages</p>
          <h2 className="ppm-h2">When you need a little more.</h2>
          <p className="ppm-body">
            Going over a cap doesn't force you up a tier.{" "}
            <strong>Extra active employee profiles</strong> are $9/month each.{" "}
            <strong>Extra candidate runs</strong> are sold as credit packs —
            $5 a run — so a hiring sprint never forces an upgrade.{" "}
            <strong>Extra seats</strong> are $49/month each. Overages are never
            auto-billed by surprise: you get a clear in-app notice when you
            cross a cap, and your existing work is never paywalled.
          </p>
        </div>
      </section>

      {/* ── What you don't pay for ── */}
      <section className="ppm-block ppm-compact ppm-tinted">
        <div className="ppm-block-inner">
          <h2 className="ppm-h2">What you don't pay for.</h2>
          <p className="ppm-body">
            No seat floors — start with one. No annual lock-in — monthly is a
            real option, annual just saves you two months. No per-assessment
            fee. No per-respondent charge. No "AI credit" pricing that breaks
            your budget the month you actually use the product.{" "}
            <strong>The price you see is the price you pay.</strong>
          </p>
        </div>
      </section>

      {/* ── Org plan note ── */}
      <section className="ppm-block ppm-compact">
        <div className="ppm-block-inner">
          <p className="ppm-eyebrow">Org Plan</p>
          <p className="ppm-body ppm-quiet">
            Larger organizations sometimes want per-employee pricing for a
            full-headcount rollout — every manager in the company on one plan,
            billed per seat. A per-employee Org Plan is in development for V2
            of PeoplePrints (target: $9 / employee / month). If a per-employee
            rollout is what you're scoping, talk to us about Enterprise today
            and we'll keep you posted on Org Plan readiness.
          </p>
        </div>
      </section>

      {/* ── Pricing FAQ ── */}
      <section className="ppm-block ppm-tinted">
        <div className="ppm-block-inner">
          <p className="ppm-eyebrow">Pricing FAQ</p>
          <h2 className="ppm-h2">The questions buyers actually ask.</h2>
          <div className="ppm-faq">
            {FAQS.map((f, i) => (
              <details className="ppm-faq-item" key={f.q} open={i === 0}>
                <summary>{f.q}</summary>
                <div className="ppm-faq-a">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ── */}
      <section className="ppm-cta">
        <div className="ppm-cta-inner">
          <h2 className="ppm-cta-h">
            Pick the plan that matches your job. Switch when your job changes.
          </h2>
          <div className="ppm-cta-actions">
            <Link to="/demo" className="ppm-btn ppm-btn-coral">
              Request a demo
            </Link>
            <Link
              to="/demo?topic=enterprise"
              className="ppm-btn ppm-btn-ghost-light"
            >
              Talk to us about Enterprise
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer — clean A6 nav ── */}
      <footer className="ppm-footer">
        <div className="ppm-footer-inner">
          <div className="ppm-footer-links">
            {FOOTER_LINKS.map((l) => (
              <Link to={l.to} key={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="ppm-footer-legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <p className="ppm-footer-copy">
            © 2026 PeoplePrints™. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

const PRICING_CSS = `
.ppm-root {
  --c-navy: var(--ppr-color-accent-navy);
  --c-steel-dark: var(--ppr-color-accent-steel-dark);
  --c-powder: var(--ppr-color-accent-powder);
  --c-pale-soft: var(--ppr-color-accent-pale-soft);
  --c-coral: var(--ppr-color-accent-coral);
  --c-coral-dark: var(--ppr-color-accent-coral-dark);
  /* --c-gold: the locked marketing mockups use a gold hero-eyebrow accent
     with no token in design-tokens.v2.json — ported as a literal, flagged
     for design (same flag as the C1 homepage rebuild). */
  --c-gold: #C99A4B;
  --c-surface: var(--ppr-color-background-surface);
  --c-text: var(--ppr-color-text-primary);
  --c-text-2: var(--ppr-color-text-secondary);
  --c-muted: var(--ppr-color-text-muted);
  --c-border: var(--ppr-color-border-default);
  --f-display: var(--ppr-typography-font-family-display);
  --f-body: var(--ppr-typography-font-family-body);
  --f-ui: var(--ppr-typography-font-family-ui);
  --f-mono: var(--ppr-typography-font-family-mono);
  font-family: var(--f-body);
  color: var(--c-text);
  line-height: 1.55;
  background: var(--c-surface);
}
.ppm-root * { box-sizing: border-box; }

.ppm-btn { display: inline-block; font-family: var(--f-ui); font-weight: 600; font-size: 15px; padding: 14px 26px; border-radius: 6px; text-decoration: none; border: 2px solid transparent; cursor: pointer; }
.ppm-btn-coral { background: var(--c-coral); color: #FFF; border-color: var(--c-coral); }
.ppm-btn-ghost-light { background: transparent; color: #FFF; border-color: rgba(255,255,255,0.55); }

.ppm-block { padding: 72px 32px; }
.ppm-block.ppm-compact { padding: 48px 32px; }
.ppm-block.ppm-tinted { background: var(--c-pale-soft); }
.ppm-block-inner { max-width: 1080px; margin: 0 auto; }
.ppm-eyebrow { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 14px; }
.ppm-h2 { font-family: var(--f-display); font-size: 30px; line-height: 1.1; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 18px; max-width: 880px; }
.ppm-body { font-family: var(--f-body); font-size: 16.5px; line-height: 1.65; color: var(--c-text); max-width: 760px; margin: 0 0 16px; }
.ppm-body strong { color: var(--c-navy); }
.ppm-body.ppm-quiet { font-size: 15px; color: var(--c-text-2); }

.ppm-hero { padding: 84px 32px 56px; background: linear-gradient(180deg, var(--c-pale-soft) 0%, var(--c-surface) 100%); }
.ppm-hero-inner { max-width: 900px; margin: 0 auto; text-align: center; }
.ppm-hero-eyebrow { font-family: var(--f-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: var(--c-gold); margin: 0 0 18px; }
.ppm-hero-h1 { font-family: var(--f-display); font-size: 44px; line-height: 1.08; letter-spacing: -0.025em; color: var(--c-navy); margin: 0 0 22px; }
.ppm-hero-sub { font-family: var(--f-body); font-size: 18px; line-height: 1.65; color: var(--c-text-2); margin: 0 auto; max-width: 720px; }

.ppm-billing-toggle { display: flex; width: fit-content; margin: 0 auto 34px; border: 2px solid var(--c-navy); border-radius: 999px; overflow: hidden; }
.ppm-bt { background: transparent; border: none; cursor: pointer; font-family: var(--f-ui); font-size: 13px; font-weight: 700; color: var(--c-steel-dark); padding: 10px 20px; display: flex; align-items: center; gap: 8px; }
.ppm-bt-on { background: var(--c-navy); color: #FFF; }
.ppm-bt-save { font-family: var(--f-ui); font-size: 9.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; background: var(--c-coral); color: #FFF; padding: 3px 7px; border-radius: 999px; }

.ppm-pricing-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; max-width: 1180px; margin: 0 auto; }
.ppm-tier { background: var(--c-surface); border: 2px solid var(--c-border); padding: 28px 22px; display: flex; flex-direction: column; position: relative; }
.ppm-tier-featured { border-color: var(--c-navy); border-top: 8px solid var(--c-coral); box-shadow: 0 8px 28px rgba(28,42,64,0.10); }
.ppm-tier-ribbon { position: absolute; top: -1px; right: -1px; background: var(--c-coral); color: #FFF; font-family: var(--f-display); font-size: 9.5px; letter-spacing: 0.10em; text-transform: uppercase; padding: 6px 12px; }
.ppm-tier-name { font-family: var(--f-display); font-size: 17px; color: var(--c-navy); margin: 0 0 8px; letter-spacing: -0.01em; }
.ppm-tier-price { font-family: var(--f-display); font-size: 30px; color: var(--c-navy); margin: 0 0 2px; letter-spacing: -0.02em; }
.ppm-tier-price .ppm-tier-period, .ppm-tier-price .ppm-tier-from { font-family: var(--f-ui); font-size: 13px; font-weight: 600; color: var(--c-muted); letter-spacing: 0; }
.ppm-tier-builtfor { font-family: var(--f-body); font-size: 13.5px; font-style: italic; line-height: 1.5; color: var(--c-text-2); margin: 10px 0 16px; padding-bottom: 14px; border-bottom: 1px solid var(--c-border); }
.ppm-tier-includes { list-style: none; padding: 0; margin: 0 0 22px; flex-grow: 1; font-family: var(--f-ui); font-size: 12.5px; line-height: 1.5; color: var(--c-text); }
.ppm-tier-includes li { padding: 7px 0 7px 20px; position: relative; }
.ppm-tier-includes li::before { content: '+'; position: absolute; left: 0; top: 6px; font-family: var(--f-mono); color: var(--c-coral); font-weight: 700; }
.ppm-tier-includes li.ppm-inherits { font-weight: 700; color: var(--c-steel-dark); border-bottom: 1px solid var(--c-border); margin-bottom: 4px; padding-bottom: 11px; }
.ppm-tier-includes li.ppm-inherits::before { content: '\\2191'; }
.ppm-tier-cta { display: block; text-align: center; padding: 12px 14px; border-radius: 6px; font-family: var(--f-display); font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase; text-decoration: none; }
.ppm-cta-primary { background: var(--c-coral); color: #FFF; border: 2px solid var(--c-coral); }
.ppm-cta-secondary { background: transparent; color: var(--c-navy); border: 2px solid var(--c-navy); }

.ppm-faq { margin: 26px 0 0; max-width: 880px; }
.ppm-faq-item { border: 1.5px solid var(--c-border); margin: 0 0 10px; background: var(--c-surface); }
.ppm-faq-item summary { list-style: none; cursor: pointer; padding: 18px 22px; font-family: var(--f-display); font-size: 15px; color: var(--c-navy); display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.ppm-faq-item summary::-webkit-details-marker { display: none; }
.ppm-faq-item summary::after { content: '+'; font-family: var(--f-mono); font-size: 18px; color: var(--c-coral); }
.ppm-faq-item[open] summary::after { content: '\\2013'; }
.ppm-faq-a { padding: 0 22px 20px; font-family: var(--f-body); font-size: 15px; line-height: 1.6; color: var(--c-text); }

.ppm-cta { background: var(--c-navy); color: #FFF; padding: 60px 32px; text-align: center; }
.ppm-cta-inner { max-width: 820px; margin: 0 auto; }
.ppm-cta-h { font-family: var(--f-display); font-size: 28px; line-height: 1.14; letter-spacing: -0.02em; margin: 0 0 26px; }
.ppm-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

.ppm-footer { background: var(--c-surface); border-top: 1px solid var(--c-border); padding: 44px 32px; }
.ppm-footer-inner { max-width: 1080px; margin: 0 auto; text-align: center; }
.ppm-footer-links { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin: 0 0 14px; font-family: var(--f-ui); font-size: 13px; font-weight: 600; }
.ppm-footer-links a { color: var(--c-text-2); text-decoration: none; }
.ppm-footer-legal { display: flex; gap: 16px; justify-content: center; margin: 0 0 16px; font-family: var(--f-ui); font-size: 12.5px; }
.ppm-footer-legal a { color: var(--c-muted); text-decoration: none; }
.ppm-footer-copy { font-family: var(--f-mono); font-size: 11.5px; color: var(--c-muted); }

@media (max-width: 980px) { .ppm-pricing-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 800px) {
  .ppm-hero-h1 { font-size: 32px; }
  .ppm-h2 { font-size: 24px; }
  .ppm-cta-h { font-size: 22px; }
  .ppm-block, .ppm-hero { padding-left: 20px; padding-right: 20px; }
}
@media (max-width: 560px) { .ppm-pricing-grid { grid-template-columns: 1fr; } }
`;
